/* eslint-disable sonarjs/no-duplicate-string */
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { PROPERTY_STATUS } from '@modules/opportunities/entities/opportunity/const';
import { t as i18t } from 'i18next';
import {
  InputGroup, InputLabel, InputDescription, Input, Text,
  Flex,
  Radio,
} from '@huspy/briks-web';
import Select from '@components/v2/Select';
import { Grid } from '@huspy/styled-system/jsx';
import { ProposalFormHandlers } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/handlers';
import { ProposalCreationClientAndMortgageForm } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/types';
import ProposalClientAndMortgageSectionBox from '../SectionBox';
import { emiratesSupportFeeFinancing } from '../MortgageDetails/utils/const';

const RATE_TYPES_COLLECTION = [
  { value: 'false', label: i18t('common.no') },
  { value: 'true', label: i18t('common.yes') }
];

const STATUS_OF_PROPERTY = [
  {
    label: i18t('clients.propertyDetails.propertyStatusReady'),
    value: PROPERTY_STATUS.ready,
  },
  {
    label: i18t('clients.propertyDetails.propertyStatusUnderConstruction'),
    value: PROPERTY_STATUS.underConstruction,
  }
];

export const STATES = [
  {
    label: 'Dubai',
    value: 'dubai',
  },
  {
    label: 'Abu Dhabi',
    value: 'abu_dhabi',
  },
  {
    label: 'Sharjah',
    value: 'sharjah',
  },
  {
    label: 'Ajman',
    value: 'ajman',
  },
  {
    label: 'Ras Al Khaimah',
    value: 'ras_al_khaimah',
  },
  {
    label: 'Fujairah',
    value: 'fujairah',
  },
  {
    label: 'Umm Al Quwain',
    value: 'umm_al_quwain',
  }
];

type Props = {
  form: ReturnType<typeof useForm<ProposalCreationClientAndMortgageForm>>;
  isDisabled?: boolean;
};

const {
  existingMortgageTypeHandler,
  propertyStatusHandler,
  transactionTypeHandler,
  propertyLocationHandler,
  isDIFCHandler,
  landDepartmentFeeFinanceHandler,
  realEstateFeeFinanceHandler,
} = ProposalFormHandlers;

export const PropertyDetails = ({ form, isDisabled }: Props) => {
  const { t } = useTranslation();

  form.watch('property_details.property_status', (data) => {
    if (data.previousValue !== data.value) {
      transactionTypeHandler(form, '');
      existingMortgageTypeHandler(form, '');
    }
  });

  form.watch('property_details.is_difc', (data) => {
    if (data.value === 'true') {
      propertyLocationHandler(form, t('proposals.proposalsDetails.DIFC'));
    } else if (form.values.property_details.property_location === t('proposals.proposalsDetails.DIFC')) {
      propertyLocationHandler(form, '');
    }
  });

  form.watch('property_details.state', (data) => {
    if (data.value !== 'dubai') {
      propertyLocationHandler(form, '');
      isDIFCHandler(form, 'false');
    }
    if (!emiratesSupportFeeFinancing.has(data.value)) {
      landDepartmentFeeFinanceHandler(form, false);
      realEstateFeeFinanceHandler(form, false);
    }
  });

  return (
    <ProposalClientAndMortgageSectionBox title={ t('proposals.proposalsDetails.propertyDetails') }>
      <Grid
        gap='6'
        columns={ 2 }
      >
        <Select
          label='Property emirate'
          placeholder='Select'
          size='lg'
          data={ STATES }
          data-test='select-property-emirate'
          disabled={ isDisabled }
          { ...form.getInputProps('property_details.state') }
          onValueChange={ (e) => { form.getInputProps('property_details.state').onChange(e.value[0]); } }
        />

        <Flex direction='column' justify='center'>
          <Radio.Root
            value={ form.values.property_details.is_difc }
            onChange={ (e) =>
              isDIFCHandler(form, (e.target as any).value) }
          >
            <Radio.Label>
              <Text color='text-secondary' size='sm' weight='medium'>
                { t('proposals.proposalsDetails.isItInDIFC') }
              </Text>
            </Radio.Label>
            <Radio.Indicator />
            <Flex gap='7'>
              {RATE_TYPES_COLLECTION.map((option) => (
                <Radio.Item
                  key={ option.value }
                  value={ option.value }
                  disabled={ isDisabled || form.values.property_details.state !== 'dubai' }
                >
                  <Radio.ItemControl />
                  <Radio.ItemText>{option.label}</Radio.ItemText>
                  <Radio.ItemHiddenInput />
                </Radio.Item>
              ))}
            </Flex>
          </Radio.Root>
        </Flex>

        <Flex direction='column'>
          <InputGroup isError={ !!form.getInputProps('property_details.property_location')?.error }>
            <InputLabel>{ t('common.forms.propertyLocation.label') }</InputLabel>
            <Input
              size='lg'
              name='property_location'
              placeholder={ t('proposals.proposalsDetails.propertyLocationEg') }
              disabled={ form.values.property_details.is_difc === 'true' || isDisabled }
              data-test='property-location'
              { ...form.getInputProps('property_details.property_location') }
            />
            <InputDescription fontSize='sm'>
              {form.getInputProps('property_details.property_location')?.error ?? t('proposals.proposalsDetails.optional')}
            </InputDescription>
          </InputGroup>
        </Flex>

        <Select
          label={ t('common.forms.stateOfProperty.label') }
          placeholder={ t('common.forms.stateOfProperty.placeholder') }
          data={ STATUS_OF_PROPERTY }
          size='lg'
          data-test='select-property-status'
          disabled={ isDisabled }
          { ...form.getInputProps('property_details.property_status') }
          onValueChange={ (e) => { propertyStatusHandler(form, e.value[0]); } }
        />
      </Grid>
    </ProposalClientAndMortgageSectionBox>
  );
};
